import { VenueResponse } from '../models/VenueResponse';
import { Localization } from '../models/Localization';
import { CheckoutIntegrationType } from '../enums/CheckoutIntegrationType';
import { Tag } from '../models/Tag';
import { OrderType } from '../enums/order/OrderType';
import { OrderMode } from '../enums/order/OrderMode';
import { Offset } from '../models/Offset';
import { Legal } from '../models/Legal';
import { MovAndFee } from '../models/MovAndFee';
import { Assets } from '../models/Assets';
import { Tip } from '../models/Tip';
import { Hour } from './Hour';
import { HourResponse, SpecialHour } from '../models/HourResponse';
import { currWeekDay, isCurrentTimeInRange } from '../../shared/utils/time';
import { ArticleCategory } from './ArticleCategory';
import { Address } from './Address';
import { calculateGeoDistance } from '../../shared/utils/geo';
import { SlotsConfig } from '../models/order/SlotsConfig';

export class Venue {
	_id: string;
	_tags?: Tag[];
	_tagsMap: Map<string, Tag>;

	idx: number;
	assets: Assets;
	masterId: string;
	articleCategories?: ArticleCategory[] = [];
	gpEnabled: boolean;
	gpPreorderEnabled: boolean;
	gpDeliveryEnabled: boolean;
	apEnabled: boolean;
	apPreorderEnabled: boolean;
	apDeliveryEnabled: boolean;
	ccEnabled: boolean;
	ccPreorderEnabled: boolean;
	ccDeliveryEnabled: boolean;
	ppFullIntegrationEnabled: boolean;
	city: Localization;
	currency: string;
	deliveryEnabled?: boolean;
	deliveryHours?: Hour[];
	deliveryRadius?: number;
	firstPreorderOffset?: number;
	lastPreorderOffset?: number;
	preorderOffset?: number;
	isPublished?: boolean;
	isServiceActivated?: boolean;
	location: { coordinates: [number, number]; type?: string };
	deliveryByRadius: boolean;
	vrPaymentEnabled: boolean;
	movDelivery?: {
		$numberDecimal: number;
	};
	name: string;
	openingHours?: Hour[];
	phoneNumber?: string;
	postalCode?: string;
	ppEnabled?: boolean;
	preorderIn: boolean;
	preorderTable?: string;
	preorderTakeAway: boolean;
	readableId?: string;
	sbEnabled?: boolean;
	street?: string;
	number?: string;
	country: string;
	slot: SlotsConfig;
	offsets: { [key in OrderType]: { [key in OrderMode]: Offset } };
	deliveryFees: [
		{
			_id: string;
			from: {
				$numberDecimal: number;
			};
			fee: {
				$numberDecimal: number;
			};
		},
	];
	deliveryFeesPostalCodes: [
		{
			_id: string;
			postalCode: string;
			fee: {
				$numberDecimal: number;
			};
		},
	];
	panicEndAt: string;
	legal: Legal;
	deliveryPostalCodes: string[];
	isPostalDelivery: false;
	movAndFee: MovAndFee;
	googlePlacesId: string;
	specialOpeningHours?: SpecialHour[];
	scheduleTemplateIsOpen?: boolean;
	isOpen: boolean;
	isMaster: boolean;
	specialDeliveryHours?: SpecialHour[];
	cashEnabled?: boolean;
	cashPreorderEnabled?: boolean;
	checkoutIntegration?: CheckoutIntegrationType;
	ccvEnabled: boolean;
	cateringHoursTakeAway: Hour[];
	externalDeliveryService?: {
		icon?: Assets | null;
		link?: string;
		name?: string;
	};
	adyenEnabled: boolean;
	adyenPreorderEnabled: boolean;
	cateringDelivery: boolean;
	cateringTakeAway: boolean;
	worldlineEnabled: boolean;
	deliveryByAreas: boolean;
	// deliveryAreas?: DeliveryArea[];
	uberCustomerId: string;
	deliveryProviders?: {
		uberDirect?: {
			batchSize: number;
			customerId: string;
			enabled: boolean;
			deliverableOrderTypes: OrderMode[];
		};
	};
	adyenCredentials: {
		merchantAccount: string;
		clientKey: string;
	};
	tips: Tip[];
	distanceToUser: number;

	get tagsMap() {
		if (!this._tagsMap) this._tagsMap = new Map(this._tags.map(t => [t._id, t]));
		return this._tagsMap;
	}

	get latLng(): { latitude: number; longitude: number } {
		return {
			latitude: this.location.coordinates[1],
			longitude: this.location.coordinates[0],
		};
	}

	constructor(venueRes: VenueResponse, userAddress: Address = null, orderMode = OrderMode.INSIDE) {
		Object.assign(this, venueRes);

		this.openingHours = this.initScheduleHours(venueRes.openingHours, venueRes.specialOpeningHours);
		this.deliveryHours = this.initScheduleHours(venueRes.deliveryHours, venueRes.specialDeliveryHours);
		this.computeIsOpen(orderMode);
		this._tags = venueRes.tags;
		this.slot.preorder.take_away = this.slot.preorder.takeAway;

		this.distanceToUser = this.computeDistanceToUser(userAddress);
	}

	initScheduleHours(hours: HourResponse[], specialHours: SpecialHour[]) {
		const formatedHours = [];

		for (let i = 0; i < hours.length; i++) {
			const formatedHour = new Hour(hours[i], currWeekDay());

			if (hours[i + 1]?.weekday === hours[i].weekday) {
				const nextFormatedHourForTheSameWorkingDay = new Hour(hours[i + 1], currWeekDay());

				const isNextHourRelatedToPreviousWorkingSession =
					nextFormatedHourForTheSameWorkingDay.openedAt.getTime() - formatedHour.closedAt.getTime() < 5 * 60 * 1000;

				if (isNextHourRelatedToPreviousWorkingSession) {
					formatedHour.closedAt = nextFormatedHourForTheSameWorkingDay.closedAt;
					i++; // skip next
				}
			}

			const specialHourRes = specialHours.find(
				sH => formatedHour.openedAt.toISOString().split('T')[0] === sH.date.split('T')[0]
			);
			if (!specialHourRes) {
				formatedHours.push(formatedHour);
				continue;
			}

			const formatedSpecialHours = [];
			const specialHour = new Hour({ ...specialHourRes, weekday: formatedHour.weekday }, currWeekDay());
			if (specialHourRes?.openedAt && specialHourRes?.isClosed) {
				if (formatedHour.openedAt < specialHour.openedAt) {
					formatedSpecialHours.push({ ...formatedHour, closedAt: specialHour.openedAt });
				}
				if (formatedHour.closedAt > specialHour.closedAt) {
					formatedSpecialHours.push({ ...formatedHour, openedAt: specialHour.closedAt });
				}
			}
			if (specialHourRes?.openedAt && !specialHourRes?.isClosed) {
				if (specialHour.closedAt < formatedHour.openedAt || specialHour.openedAt > formatedHour.closedAt) {
					formatedSpecialHours.push(specialHour);
				} else {
					formatedSpecialHours.push({
						...formatedHour,
						openedAt: new Date(Math.min(formatedHour.openedAt.getTime(), specialHour.openedAt.getTime())),
						closedAt: new Date(Math.max(formatedHour.closedAt.getTime(), specialHour.closedAt.getTime())),
					});
				}
			}

			if (formatedSpecialHours.length) formatedHours.push(...formatedSpecialHours);
		}

		//sorting from current day
		return formatedHours.sort((a, b) => ((a.weekday - currWeekDay() + 7) % 7) - ((b.weekday - currWeekDay() + 7) % 7));
	}

	computeDistanceToUser(userAddress: Address) {
		if (!userAddress) return null;
		return calculateGeoDistance([this.latLng.latitude, this.latLng.longitude], [userAddress.lat, userAddress.lng]);
	}

	computeIsOpen(orderMode = OrderMode.INSIDE) {
		const hours = orderMode === OrderMode.DELIVERY ? this.deliveryHours : this.openingHours;
		return (this.isOpen =
			this.isServiceActivated &&
			hours?.length &&
			(!this.panicEndAt || new Date(this.panicEndAt).getTime() < Date.now()) &&
			hours.some(h => (h.weekday === currWeekDay() - 1 || 6) && isCurrentTimeInRange(h)));
	}
}
